import { ChakraProps, Flex, Input, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getCurrencySymbol, isPrice, Price } from "@/features/types";

export interface PriceInputValue {
  value?: number;
  currency: Price["currency"] | "EA" | "%";
}

function isPriceInputValue(candidate: any): candidate is PriceInputValue {
  return (
    candidate &&
    (candidate.value === undefined || typeof candidate.value === "number") &&
    candidate.currency
  );
}

interface AppPriceInputProps {
  value?: PriceInputValue;
  currency?: PriceInputValue["currency"];
  currencyWidth?: string;
  onChange?: (value?: PriceInputValue) => void;
  isDisabled?: boolean;
  width?: string;
  isReadOnly?: boolean;
  isLiteral?: boolean;
  showComma?: boolean;
}

// 정수부에만 콤마를 넣고 소수부는 입력 그대로 보존한다 ("1000." → "1,000.")
function formatWithCommas(raw: string): string {
  const [intPart, decPart] = raw.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

function calcCurrencyStyle({
  isDisabled,
  isReadOnly,
}: Pick<AppPriceInputProps, "isDisabled" | "isReadOnly">): ChakraProps {
  const defaultStyle: ChakraProps = {
    borderRadius: "0 4px 4px 0",
    borderWidth: "0.5px",
    borderStyle: "solid",
    borderColor: "#8F8F8C",
    backgroundColor: "#FCFCFB",
  };

  if (isDisabled) {
    return {
      ...defaultStyle,
      borderColor: "#8F8F8C",
      backgroundColor: "#EFEFED",
      color: "#444444",
    };
  } else if (isReadOnly) {
    return {
      ...defaultStyle,
      borderColor: "#6F6F6C",
      backgroundColor: "#FCFCFB",
      color: "#6F6F6C",
    };
  }

  return defaultStyle;
}

function calcInputStyle({
  isDisabled,
  isReadOnly,
  isLiteral,
}: AppPriceInputProps): ChakraProps {
  const defaultStyle: ChakraProps = {
    borderRadius: "4px 0px 0px 4px",
    borderTop: "0.5px solid var(--Gray-Groomy, #8F8F8C)",
    borderBottom: "0.5px solid var(--Gray-Groomy, #8F8F8C)",
    borderLeft: "0.5px solid var(--Gray-Groomy, #8F8F8C)",
    background: "var(--White, #FFF)",
    _hover: {},
  };

  if (isDisabled) {
    return {
      ...defaultStyle,
      background: "var(--pathrade-gray-light, #EFEFED)",
      // 비활성화 금액도 또렷하게 보이도록 진한 텍스트 + Chakra 기본 흐림(opacity 0.4) 해제
      color: "#444444",
      opacity: 1,
      _disabled: { opacity: 1, color: "#444444", cursor: "not-allowed" },
    };
  } else if (isReadOnly) {
    return {
      ...defaultStyle,
      borderColor: "#6F6F6C",
      backgroundColor: "#FFF",
      borderRight: "none",
      color: "#6F6F6C",
      opacity: "1.0 !important",
    };
  }

  return defaultStyle;
}

export default function AppPriceInput({
  value: _value,
  currency: _currency = "EUR",
  onChange = () => {},
  width = "100%",
  isDisabled = false,
  isReadOnly = false,
  currencyWidth,
  isLiteral = false,
  showComma = false,
}: AppPriceInputProps) {
  const { tNumberString, tCurrencyString } = useI18nHelper();
  const [value, symbol] = useMemo(() => {
    if (_value !== undefined) {
      return [_value.value, getCurrencySymbol(_value.currency)];
    }

    return [undefined, getCurrencySymbol(_currency)];
  }, [_currency, _value]);

  const readableValue = useMemo(() => {
    if (value !== undefined) {
      if (isReadOnly && _currency !== "EA") {
        return tCurrencyString(value, { type: "NONE" });
      } else if (isLiteral) {
        return tCurrencyString(value, { type: "SYMBOL", code: _currency });
      }
      return value;
    }
    return "";
  }, [_currency, isReadOnly, isLiteral, tCurrencyString, value]);

  // 편집 모드 표시용 문자열: 타이핑 중간 상태("1,000.")를 보존하기 위해 로컬로 관리
  const [displayValue, setDisplayValue] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  // 콤마 재포맷으로 커서가 끝으로 튀지 않도록, 리렌더 후 복원할 커서 위치
  const cursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
      cursorRef.current = null;
    }
  }, [displayValue]);

  useEffect(() => {
    if (!showComma) {
      return;
    }
    const parsed = parseFloat(displayValue.replace(/,/g, ""));
    const normalized = isNaN(parsed) ? undefined : parsed;
    if (normalized !== value) {
      setDisplayValue(
        value !== undefined ? formatWithCommas(String(value)) : ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComma, value]);

  if (isLiteral) {
    return (
      <Text fontSize={"12px"} textAlign={"start"}>
        {readableValue.toLocaleString()}
      </Text>
    );
  }

  return (
    <Flex height={"28px"} fontSize={"12px"} flexDirection={"row"} width={width}>
      <Input
        ref={inputRef}
        padding={"0 8px"}
        margin={"0"}
        minHeight={"0"}
        height={"auto"}
        fontSize={"inherit"}
        type={isReadOnly || showComma ? "text" : "number"}
        inputMode={showComma ? "decimal" : undefined}
        value={
          isReadOnly ? readableValue : showComma ? displayValue : readableValue
        }
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        placeholder={"0"}
        textAlign={"end"}
        {...calcInputStyle({ isDisabled, isReadOnly })}
        onChange={(event) => {
          const raw = showComma
            ? event.target.value.replace(/,/g, "")
            : event.target.value;
          if (showComma) {
            // 숫자/소수점/음수 외의 입력은 무시
            if (!/^-?\d*\.?\d*$/.test(raw)) {
              return;
            }
            const el = event.target;
            const cursor = el.selectionStart ?? el.value.length;
            // 커서 왼쪽의 콤마 제외 문자 수를 기준으로, 재포맷된 문자열에서의 커서 위치를 계산
            const charsBeforeCursor = el.value
              .slice(0, cursor)
              .replace(/,/g, "").length;
            const formatted = formatWithCommas(raw);
            let pos = 0;
            let count = 0;
            while (pos < formatted.length && count < charsBeforeCursor) {
              if (formatted[pos] !== ",") count++;
              pos++;
            }
            if (formatted === displayValue) {
              // 콤마만 삭제된 경우: state가 그대로라 리렌더가 없으므로 DOM을 직접 복구
              el.value = formatted;
              el.setSelectionRange(pos, pos);
            } else {
              cursorRef.current = pos;
              setDisplayValue(formatted);
            }
          }
          let value: number | undefined = parseFloat(raw);
          if (isNaN(value)) {
            value = undefined;
          }
          onChange({
            value,
            currency: _currency,
          });
        }}
      />
      <Text
        padding={"0 8px"}
        alignSelf={"stretch"}
        display={"inline-flex"}
        alignItems={"center"}
        width={currencyWidth}
        {...calcCurrencyStyle({ isDisabled, isReadOnly })}
      >
        {symbol}
      </Text>
    </Flex>
  );
}
